import { useState, useEffect } from 'react';
import { Icon, Tooltip } from '@repo/ui';
import { usePreviewFileQuery } from '../../queries';
import { MatterInvoiceSummaryFile } from '../../types';

interface ClickableFileButtonProps {
  file: MatterInvoiceSummaryFile;
}

export function ClickableFileButton({ file }: Readonly<ClickableFileButtonProps>) {
  const [isClicked, setIsClicked] = useState(false);

  const { isLoading, data: document } = usePreviewFileQuery(file.fileId, {
    enabled: isClicked,
  });

  useEffect(() => {
    if (document?.data && isClicked) {
      window.open(document.data, '_blank');
      setIsClicked(false);
    }
  }, [document, isClicked]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClicked(true);
  };

  const lowerName = file.fileName.toLowerCase();
  const iconName = lowerName.endsWith('.pdf')
    ? 'HiDocumentText'
    : lowerName.endsWith('.eml')
      ? 'HiMail'
      : 'HiPhotograph';

  return (
    <Tooltip content={file.fileName}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading}
        className="flex items-center gap-2 px-3 py-2 bg-gray-300 dark:bg-gray-800/50 rounded border border-gray-400 dark:border-gray-700 overflow-hidden hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors disabled:opacity-70 disabled:cursor-wait text-left max-w-[200px]"
      >
        {isLoading ? (
          <Icon name="FaSpinner" className="animate-spin text-gray-500 flex-shrink-0" size="sm" />
        ) : (
          <Icon name={iconName} className="text-gray-500 flex-shrink-0" size="sm" />
        )}
        <span className="text-sm text-gray-700 dark:text-gray-300 truncate font-medium">
          {file.fileName}
        </span>
      </button>
    </Tooltip>
  );
}
